import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import {
  FUNCOES,
  alterarPessoa,
  definirAdmissao,
  definirFolgaFixa,
  diaLongo,
  hojeBrasilia,
  lerFerias,
  lerHistorico,
} from '../../escala';
import type {
  AlteracaoDaEscala,
  FeriasDaPessoaNaEscala,
  PessoaDaEscala,
  TurnoDaEscala,
} from '../../escala';
import { EditorDaFolgaFixa } from './EditorDaFolgaFixa';
import { MetasDaPessoa } from './EscalaMetas';
import { DetalheDeFerias } from './EscalaFerias';

/**
 * FICHA DO FUNCIONÁRIO (dono, 24/10/2026): "quando eu apertar no lápis, editar tudo daquele
 * funcionário — aniversário, função… e a folga fixa ficar dentro do lápis, em partes: dados do
 * funcionário, dados de férias entre outras coisas".
 */

export type ParteDaFicha = 'dados' | 'horario' | 'folga' | 'ferias' | 'metas' | 'historico';
const PARTES: readonly {
  id: ParteDaFicha;
  rotulo: string;
  icone: 'user' | 'cal' | 'clock' | 'file' | 'gauge';
}[] = [
  { id: 'dados', rotulo: 'Dados do funcionário', icone: 'user' },
  { id: 'horario', rotulo: 'Horário', icone: 'clock' },
  { id: 'folga', rotulo: 'Folga fixa', icone: 'cal' },
  { id: 'ferias', rotulo: 'Férias', icone: 'clock' },
  { id: 'metas', rotulo: 'Metas', icone: 'gauge' },
  { id: 'historico', rotulo: 'Histórico', icone: 'file' },
];
const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

export function FichaDaPessoa({
  sessao,
  pessoa,
  parte: parteInicial,
  turnos,
  contas,
  aoFechar,
  aoMudar,
}: {
  sessao: Sessao;
  pessoa: PessoaDaEscala;
  parte: ParteDaFicha;
  turnos: readonly TurnoDaEscala[];
  contas: readonly { accountId: string; nome: string; login: string }[];
  aoFechar: () => void;
  /** Algo foi salvo: recarregar a lista (com o texto do aviso). */
  aoMudar: (texto: string) => void;
}) {
  const [parte, setParte] = useState<ParteDaFicha>(parteInicial);
  const [p, setP] = useState(pessoa);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const avisar = (texto: string) => {
    setErro(null);
    setOk(texto);
    aoMudar(texto);
  };
  const falhar = (r: { tipo: string; mensagem?: string }) =>
    setErro(
      r.tipo === 'recusada' ? (r.mensagem ?? 'Não foi aceito.') : 'A internet caiu — confira.',
    );

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal qh-ficha"
        role="dialog"
        aria-modal="true"
        aria-label={`Ficha de ${p.nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>{p.nome}</h2>
            <p>
              {p.funcao ?? 'Sem função'}
              {p.accountId ? ' · com conta no InovaPay' : ' · só nome (sem conta)'}
            </p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        <nav className="qh-ficha-abas" aria-label="Partes da ficha">
          {PARTES.map((x) => (
            <button
              key={x.id}
              type="button"
              className={parte === x.id ? 'qh-ficha-aba qh-ficha-aba-on' : 'qh-ficha-aba'}
              aria-pressed={parte === x.id}
              onClick={() => {
                setParte(x.id);
                setOk(null);
                setErro(null);
              }}
            >
              <Icone nome={x.icone} tamanho={14} />
              {x.rotulo}
            </button>
          ))}
        </nav>
        {erro && (
          <p className="qh-aviso qh-aviso-erro" role="alert">
            {erro}
          </p>
        )}
        {ok && (
          <p className="qh-aviso qh-aviso-ok" role="status">
            {ok}
          </p>
        )}

        {parte === 'dados' && (
          <DadosDoFuncionario
            sessao={sessao}
            p={p}
            contas={contas}
            aoSalvar={(np, texto) => {
              setP(np);
              avisar(texto);
            }}
            aoErro={falhar}
          />
        )}
        {parte === 'horario' && (
          <HorarioDoFuncionario
            sessao={sessao}
            p={p}
            turnos={turnos}
            aoSalvar={(np, texto) => {
              setP(np);
              avisar(texto);
            }}
            aoErro={falhar}
          />
        )}
        {parte === 'folga' && (
          <EditorDaFolgaFixa
            key={p.id}
            embutido
            pessoa={p}
            turnos={turnos}
            aoFechar={aoFechar}
            aoSalvar={async (f) => {
              const r = await definirFolgaFixa(sessao, p.id, f);
              if (r.tipo === 'confirmada') {
                setP(r.dados);
                avisar(
                  f
                    ? `${p.nome}: folga fixa salva — o quadro já seguiu o cadastro de hoje em diante${r.dados.diasTrocados ? ` (${String(r.dados.diasTrocados)} dia${r.dados.diasTrocados === 1 ? '' : 's'} mudaram)` : ' (nada a mudar)'}. Férias, faltas, atestados, folgas de feriado e pedidos aprovados ficaram como estavam.`
                    : `${p.nome}: sem folga fixa.`,
                );
              } else falhar(r);
            }}
          />
        )}
        {parte === 'ferias' && (
          <FeriasDaFicha sessao={sessao} p={p} aoMudar={avisar} aoErro={falhar} />
        )}
        {parte === 'metas' && <MetasDaPessoa accountId={p.accountId} pessoaId={p.id} />}
        {parte === 'historico' && <HistoricoDaPessoa pessoaId={p.id} turnos={turnos} />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Dados do funcionário
 * ------------------------------------------------------------------ */

function DadosDoFuncionario({
  sessao,
  p,
  contas,
  aoSalvar,
  aoErro,
}: {
  sessao: Sessao;
  p: PessoaDaEscala;
  contas: readonly { accountId: string; nome: string; login: string }[];
  aoSalvar: (p: PessoaDaEscala, texto: string) => void;
  aoErro: (r: { tipo: string; mensagem?: string }) => void;
}) {
  const conhecida = p.funcao === null || FUNCOES.includes(p.funcao);
  const [nome, setNome] = useState(p.nome);
  const [funcao, setFuncao] = useState(conhecida ? (p.funcao ?? '') : '__outra');
  const [outra, setOutra] = useState(conhecida ? '' : (p.funcao ?? ''));
  const [dia, setDia] = useState(p.aniversario ? String(p.aniversario.dia) : '');
  const [mes, setMes] = useState(p.aniversario ? String(p.aniversario.mes) : '');
  const [conta, setConta] = useState(p.accountId ?? '');
  const [admissao, setAdmissao] = useState(p.admissao ?? '');
  const [salvando, setSalvando] = useState(false);

  const funcaoFinal = funcao === '__outra' ? outra.trim() || null : funcao || null;
  const aniversario = dia && mes ? { dia: Number(dia), mes: Number(mes) } : null;
  const aniversarioIncompleto = (dia === '') !== (mes === '');

  const salvar = async () => {
    setSalvando(true);
    const r = await alterarPessoa(sessao, p.id, {
      nome: nome.trim(),
      funcao: funcaoFinal,
      aniversario,
      ...(conta !== (p.accountId ?? '') ? { accountId: conta || null } : {}),
    });
    if (r.tipo !== 'confirmada') {
      setSalvando(false);
      return aoErro(r);
    }
    let atual = r.dados;
    if ((admissao || null) !== p.admissao) {
      const a = await definirAdmissao(sessao, p.id, admissao || null);
      if (a.tipo !== 'confirmada') {
        setSalvando(false);
        return aoErro(a);
      }
      atual = { ...atual, admissao: admissao || null };
    }
    setSalvando(false);
    aoSalvar(atual, `${atual.nome}: dados salvos.`);
  };

  return (
    <div className="qh-ficha-parte">
      <div className="qh-linha-campos">
        <label className="qh-campo">
          Nome no quadro
          <input value={nome} maxLength={40} onChange={(e) => setNome(e.target.value)} />
        </label>
        <label className="qh-campo">
          Função
          <select value={funcao} onChange={(e) => setFuncao(e.target.value)}>
            <option value="">— sem função —</option>
            {FUNCOES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
            <option value="__outra">Outra…</option>
          </select>
        </label>
        {funcao === '__outra' && (
          <label className="qh-campo">
            Qual função?
            <input
              value={outra}
              maxLength={30}
              onChange={(e) => setOutra(e.target.value)}
              placeholder="ex.: Entregador"
            />
          </label>
        )}
      </div>
      <div className="qh-linha-campos">
        <label className="qh-campo">
          Aniversário · dia
          <select value={dia} onChange={(e) => setDia(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 31 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </label>
        <label className="qh-campo">
          Aniversário · mês
          <select value={mes} onChange={(e) => setMes(e.target.value)}>
            <option value="">—</option>
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="qh-campo">
          Data de admissão
          <input
            type="date"
            value={admissao}
            max={hojeBrasilia()}
            onChange={(e) => setAdmissao(e.target.value)}
          />
        </label>
      </div>
      <label className="qh-campo">
        Conta do InovaPay (para ver a própria escala e fazer pedidos)
        <select value={conta} onChange={(e) => setConta(e.target.value)}>
          <option value="">Sem conta (só o nome)</option>
          {p.accountId && <option value={p.accountId}>A conta ligada agora</option>}
          {contas.map((c) => (
            <option key={c.accountId} value={c.accountId}>
              {c.nome} · cód {c.login}
            </option>
          ))}
        </select>
      </label>
      {aniversarioIncompleto && <p className="qh-mudo">Escolha o dia e o mês do aniversário.</p>}
      <footer>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          onClick={() => void salvar()}
          disabled={
            salvando ||
            nome.trim().length < 2 ||
            aniversarioIncompleto ||
            (funcao === '__outra' && outra.trim().length < 2)
          }
        >
          {salvando ? 'Salvando…' : 'Salvar dados'}
        </button>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Férias da pessoa (o mesmo detalhe da aba Férias)
 * ------------------------------------------------------------------ */

function FeriasDaFicha({
  sessao,
  p,
  aoMudar,
  aoErro,
}: {
  sessao: Sessao;
  p: PessoaDaEscala;
  aoMudar: (texto: string) => void;
  aoErro: (r: { tipo: string; mensagem?: string }) => void;
}) {
  const [f, setF] = useState<FeriasDaPessoaNaEscala | null | undefined>(undefined);
  const carregar = useCallback(() => {
    void lerFerias().then((r) =>
      setF(r.ok ? (r.dados.pessoas.find((x) => x.pessoaId === p.id) ?? null) : null),
    );
  }, [p.id]);
  useEffect(carregar, [carregar]);
  if (f === undefined) return <div className="qh-carregando">Carregando as férias…</div>;
  if (f === null) return <p className="qh-mudo">Não achei as férias desta pessoa.</p>;
  return (
    <div className="qh-ficha-parte">
      {f.ferias && (
        <p className="qh-previa">
          <strong>Saldo: {f.ferias.saldo} dias</strong>
          {f.ferias.proximoVencimento &&
            ` · tirar até ${diaLongo(f.ferias.proximoVencimento.limite)}`}
        </p>
      )}
      <DetalheDeFerias
        p={f}
        hoje={hojeBrasilia()}
        sessao={sessao}
        aoMudar={(np, texto) => {
          if (np) setF(np);
          else carregar();
          aoMudar(texto);
        }}
        aoErro={(texto) => aoErro({ tipo: 'recusada', mensagem: texto })}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Histórico da pessoa
 * ------------------------------------------------------------------ */

function HistoricoDaPessoa({
  pessoaId,
  turnos,
}: {
  pessoaId: string;
  turnos: readonly TurnoDaEscala[];
}) {
  const [h, setH] = useState<AlteracaoDaEscala[] | null>(null);
  useEffect(() => {
    void lerHistorico(pessoaId).then((r) => setH(r.ok ? r.dados.alteracoes : []));
  }, [pessoaId]);
  const nome = (x: AlteracaoDaEscala['antes']) =>
    x ? (turnos.find((t) => t.codigo === x.turno)?.rotulo ?? x.turno) : 'vazio';
  if (h === null) return <div className="qh-carregando">Carregando…</div>;
  if (h.length === 0) return <p className="qh-mudo">Nada mudou ainda nas células desta pessoa.</p>;
  return (
    <ul className="qh-historico qh-ficha-parte">
      {h.slice(0, 100).map((x) => (
        <li key={x.id}>
          <span className="qh-hist-quando">
            {new Date(x.em).toLocaleString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </span>
          <span>
            {diaLongo(x.dia)}: {nome(x.antes)} → <strong>{nome(x.depois)}</strong> · {x.por}
            {x.pedidoId && <small className="qh-tag-conta">pedido</small>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ *
 * Horário (dono 24/10): "hora de chegada, almoço, turno de abertura, fechamento, turno da manhã,
 * do dia, da noite — tudo organizado"
 * ------------------------------------------------------------------ */

const diasTrocados = (n: number | undefined, rotulo: string | undefined) =>
  n
    ? ` ${String(n)} dia${n === 1 ? '' : 's'} de hoje em diante passaram para “${rotulo ?? 'o turno novo'}”.`
    : '';

function HorarioDoFuncionario({
  sessao,
  p,
  turnos,
  aoSalvar,
  aoErro,
}: {
  sessao: Sessao;
  p: PessoaDaEscala;
  turnos: readonly TurnoDaEscala[];
  aoSalvar: (p: PessoaDaEscala, texto: string) => void;
  aoErro: (r: { tipo: string; mensagem?: string }) => void;
}) {
  const h = p.horario;
  const [turno, setTurno] = useState(p.turnoPadrao ?? '');
  const [entrada, setEntrada] = useState(h.entrada ?? '');
  const [almocoInicio, setAlmocoInicio] = useState(h.almocoInicio ?? '');
  const [almocoFim, setAlmocoFim] = useState(h.almocoFim ?? '');
  const [saida, setSaida] = useState(h.saida ?? '');
  const [abre, setAbre] = useState(h.abreLoja);
  const [fecha, setFecha] = useState(h.fechaLoja);
  const [salvando, setSalvando] = useState(false);
  const t = turnos.find((x) => x.codigo === turno);
  const almocoPela = (almocoInicio === '') !== (almocoFim === '');

  // escolher o turno sugere o horário dele (quando a pessoa ainda não tem horário próprio)
  const escolherTurno = (codigo: string) => {
    setTurno(codigo);
    const n = turnos.find((x) => x.codigo === codigo);
    if (!n) return;
    if (!entrada && n.entrada) setEntrada(n.entrada);
    if (!saida && n.saida) setSaida(n.saida);
    if (!almocoInicio && !almocoFim && n.almocoInicio && n.almocoFim) {
      setAlmocoInicio(n.almocoInicio);
      setAlmocoFim(n.almocoFim);
    }
  };

  const salvar = async () => {
    setSalvando(true);
    const r = await alterarPessoa(sessao, p.id, {
      turnoPadrao: turno || null,
      horario: {
        entrada: entrada || null,
        almocoInicio: almocoInicio || null,
        almocoFim: almocoFim || null,
        saida: saida || null,
        abreLoja: abre,
        fechaLoja: fecha,
      },
    });
    setSalvando(false);
    if (r.tipo === 'confirmada')
      aoSalvar(
        r.dados,
        `${p.nome}: horário salvo.${diasTrocados(r.dados.diasTrocados, t?.rotulo)}`,
      );
    else aoErro(r);
  };

  return (
    <div className="qh-ficha-parte">
      <label className="qh-campo">
        Turno de sempre
        <select value={turno} onChange={(e) => escolherTurno(e.target.value)}>
          <option value="">— sem turno fixo —</option>
          {turnos.map((x) => (
            <option key={x.codigo} value={x.codigo}>
              {x.rotulo}
              {x.entrada || x.saida ? ` (${x.entrada ?? '?'}–${x.saida ?? '?'})` : ''}
            </option>
          ))}
        </select>
        <small className="qh-ajuda">
          Trocar o turno muda a cor de todos os X desta pessoa de hoje em diante (folgas, férias e
          faltas ficam como estão). É o mesmo turno da folga fixa.
        </small>
      </label>
      <div className="qh-horario-grade">
        <label className="qh-campo">
          Chegada
          <input type="time" value={entrada} onChange={(e) => setEntrada(e.target.value)} />
        </label>
        <label className="qh-campo">
          Almoço · sai
          <input
            type="time"
            value={almocoInicio}
            onChange={(e) => setAlmocoInicio(e.target.value)}
          />
        </label>
        <label className="qh-campo">
          Almoço · volta
          <input type="time" value={almocoFim} onChange={(e) => setAlmocoFim(e.target.value)} />
        </label>
        <label className="qh-campo">
          Saída
          <input type="time" value={saida} onChange={(e) => setSaida(e.target.value)} />
        </label>
      </div>
      {t && (t.entrada || t.saida) && (
        <p className="qh-mudo">
          Horário geral do turno “{t.rotulo}”: {t.entrada ?? '?'}–{t.saida ?? '?'}
          {t.almocoInicio && t.almocoFim ? ` · almoço ${t.almocoInicio}–${t.almocoFim}` : ''}. O
          horário acima é o desta pessoa e vale mais que o do turno.
        </p>
      )}
      <div className="qh-linha-campos">
        <label className="qh-check">
          <input type="checkbox" checked={abre} onChange={(e) => setAbre(e.target.checked)} />
          <span>
            <strong>Abre a loja</strong>
            <small>Faz a abertura (chega antes, liga o sistema, abre o caixa).</small>
          </span>
        </label>
        <label className="qh-check">
          <input type="checkbox" checked={fecha} onChange={(e) => setFecha(e.target.checked)} />
          <span>
            <strong>Fecha a loja</strong>
            <small>Faz o fechamento (fecha o caixa e a loja).</small>
          </span>
        </label>
      </div>
      {almocoPela && (
        <p className="qh-mudo">Ponha a saída e a volta do almoço (ou nenhuma das duas).</p>
      )}
      <footer>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          onClick={() => void salvar()}
          disabled={salvando || almocoPela}
        >
          {salvando ? 'Salvando…' : 'Salvar horário'}
        </button>
      </footer>
    </div>
  );
}
